import React, { useState, useContext, createContext, useEffect } from 'react';
import { Shield, Eye, EyeOff, Mail, Lock, User, Building, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import {
  apiRequest,
  testApiConnection,
  getEnvironmentInfo
} from '../../utils/api';
import ForgotPasswordComponent from './ForgotPasswordComponent';
import FormSelect from '../shared/FormSelect';
import DocxsAiLogo from '../shared/DocxsAiLogo';
import { isAdminRole, ACCOUNT_TYPE_OPTIONS } from '../../utils/userPermissions';

// Development mode flag - should match the one in App.js
const DEV_MODE = false;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('docxsai-token');
    if (token) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const data = await apiRequest('auth/me');
      if (data) {
        setUser(data);
        setIsAuthenticated(true);
        setIsAdmin(isAdminRole(data.role));
      }
    } catch (err) {
      setError(err.message);
      logout(true); // Token might be invalid, so log out and redirect
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const data = await apiRequest('auth/login', {
        method: 'POST',
        body: { email, password },
      });
      
      if (data.userExistButNotVerified) {
        const message = "Verify your email to continue. Didn’t get the link? Resend link.";
        toast.info(message);
        setError(message);
        return { success: false, error: message, needsVerification: true };
      }
      if (data.access_token) {
        localStorage.setItem('docxsai-token', data.access_token);
        await fetchUserInfo(); // Fetch user info to update state
        setError(null);
        toast.success(`Welcome back, ${data.user.full_name.split(' ')[0]}!`);
        return { success: true, user: data.user };
      }
      throw new Error(data.detail || data.message || "Login failed");
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "An unknown error occurred.");
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const data = await apiRequest('auth/register', {
        method: 'POST',
        body: userData,
      });

      // Registration successful - show message and redirect to login
      if (data.message) {
        setError(null);
        toast.success(data.message);
        return { success: true, message: data.message, needsVerification: true };
      }
      // Handle detailed validation errors from FastAPI
      if (data.detail && Array.isArray(data.detail)) {
        const errorMsg = data.detail.map(d => d.msg).join(' ');
        throw new Error(errorMsg);
      }
      throw new Error(data.detail || data.message || "Registration failed");
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "An unknown error occurred.");
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = (redirectToLogin = false) => {
    localStorage.removeItem('docxsai-token');
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    toast.success('Signed out successfully');
    // Redirect to home or login page after logout
    if (redirectToLogin) {
      window.location.href = '/';
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };

  const value = {
    user,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    updateUser,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
export const LoginComponent = ({ onSwitchToRegister, onSuccess, isPage, onSwitchToForgot }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await apiRequest('auth/resend-verification', {
        method: 'POST',
        body: { email: formData.email }
      });
      toast.success('Verification email sent! Please check your inbox.');
      setNeedsVerification(false);
      setError('');
    } catch (err) {
      toast.error(err.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      if (isPage) {
        navigate('/dashboard');
      } else {
        onSuccess?.();
      }
    } else {
      setError(result.error);
      if (result.needsVerification) {
        setNeedsVerification(true);
      }
    }
    
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6 sm:mb-8">
        {/* Only show brand logo in modal mode */}
        {!isPage && (
          <div className="flex justify-center mb-3 sm:mb-4">
            <DocxsAiLogo className="h-12 w-auto mx-auto" />
          </div>
        )}
        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">Welcome Back</h2>
        <p className="text-sm sm:text-base text-[var(--color-text-muted)] mt-2">Sign in to your DocxsAi account</p>
      </div>

      {error && (
        <div className={`mb-6 p-3 rounded-lg flex items-start ${needsVerification ? 'bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30' : 'bg-[var(--color-danger)]/10 border border-[var(--color-danger)]/30'}`}>
          <AlertCircle className={`w-5 h-5 mr-3 mt-0.5 ${needsVerification ? 'text-[var(--color-warning)]' : 'text-[var(--color-danger)]'}`} />
          <div className="flex-1">
            <span className={`text-sm ${needsVerification ? 'text-[var(--color-warning)]' : 'text-[var(--color-danger)]'}`}>
              {needsVerification ? 'Verify your email to continue. Didn’t get the link?' : error}
            </span>
            {needsVerification && (
              <button
                onClick={handleResendVerification}
                disabled={resending}
                className="block mt-2 text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-sm font-medium underline disabled:opacity-50"
              >
                {resending ? 'Sending...' : 'Resend link.'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-[var(--color-text-muted)]" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-[var(--color-text)] placeholder-[var(--color-placeholder)] text-sm sm:text-base"
              placeholder="Enter your email"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full pl-12 pr-12 py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {/* Forgot password link */}
          <div className="mt-2 text-right">
            <button
              type="button"
              className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-sm font-medium focus:outline-none"
              onClick={() => {
                if (isPage) {
                  const event = new CustomEvent('docxsai-switch-auth-mode', { detail: { mode: 'forgot-password' } });
                  window.dispatchEvent(event);
                } else if (onSwitchToForgot) {
                  onSwitchToForgot();
                }
              }}
            >
              Forgot your password?
            </button>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full brand-gradient-btn py-2.5 sm:py-3 px-4 rounded-lg text-sm sm:text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-[var(--color-text-inverse)] border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              Sign In
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
            </>
          )}
        </button>
      </div>

      <div className="mt-6 text-center">
        {/* Only show toggle link in modal mode */}
        {!isPage && (
          <p className="text-[var(--color-text-muted)]">
            Don't have an account?{' '}
            <button
              onClick={onSwitchToRegister}
              className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium"
            >
              Sign up for free
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

// Register Component
export const RegisterComponent = ({ onSwitchToLogin, onSuccess, isPage }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    role: 'regular',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  const { register } = useAuth();
  const selectedAccountType = ACCOUNT_TYPE_OPTIONS.find(
    (option) => option.value === formData.role
  );

  // const freePlanFeatures = [
  //   '3 contract analyses per month',
  //   'Basic risk detection',
  //   'Email support'
  // ];

  // useEffect(() => {
  //   const featureInterval = setInterval(() => {
  //     setCurrentFeatureIndex(prevIndex => (prevIndex + 1) % freePlanFeatures.length);
  //   }, 3000);
  //   return () => clearInterval(featureInterval);
  // }, []);

  const handleNext = () => {
    if (step === 1) {
      if (!formData.full_name.trim()) {
        setError('Please enter your full name.');
        return;
      }
      if (formData.role === 'business' && !formData.company_name.trim()) {
        setError('Company name is required for business accounts.');
        return;
      }
      setError('');
      setStep(2);
    }
  };
  
  const handleBack = () => {
    setStep(step - 1);
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms and Privacy Policy.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const result = await register({
      email: formData.email,
      password: formData.password,
      full_name: formData.full_name,
      role: formData.role,
      company_name:
        formData.role === 'business' ? formData.company_name.trim() : null,
    });
    
    if (result.success) {
      if (result.needsVerification) {
        // Show verification message - no redirect needed
        setError(result.message);
      } else {
        onSuccess?.();
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6 sm:mb-8">
        {/* Only show brand logo in modal mode */}
        {!isPage && (
          <div className="flex justify-center mb-3 sm:mb-4">
            <DocxsAiLogo className="h-12 w-auto mx-auto" />
          </div>
        )}
        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">Create Account</h2>
        <p className="text-sm sm:text-base text-[var(--color-text-muted)] mt-2">Start your document optimisation today</p>
      </div>

      {error && (
        <div className={`mb-4 p-3 rounded-lg flex items-center ${
          error.includes('Registration Successful') || error.includes('verification') 
            ? 'bg-[var(--color-success)]/10 border border-[var(--color-success)]/30' 
            : 'bg-[var(--color-danger)]/10 border border-[var(--color-danger)]/30'
        }`}>
          {error.includes('Registration Successful') || error.includes('verification') ? (
            <CheckCircle className="w-5 h-5 text-[var(--color-success)] mr-3" />
          ) : (
            <AlertCircle className="w-5 h-5 text-[var(--color-danger)] mr-3" />
          )}
          <span className={`text-sm ${
            error.includes('Registration Successful') || error.includes('verification')
              ? 'text-[var(--color-success)]'
              : 'text-[var(--color-danger)]'
          }`}>{error}</span>
        </div>
      )}
      
      {/* Progress Bar */}
      <div className="w-full bg-[var(--color-surface-muted)] rounded-full h-1.5 mb-6">
        <div
          className="bg-[var(--color-primary)] h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${step === 1 ? '50%' : '100%'}` }}
        />
      </div>

      <div className="space-y-6">
        {step === 1 && (
          <>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                I am signing up as
              </label>
              <FormSelect
                value={formData.role}
                placeholder="Select account type"
                options={ACCOUNT_TYPE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, role: value }))
                }
              />
              {selectedAccountType ? (
                <p className="text-xs text-[var(--color-text-muted)] mt-2">{selectedAccountType.description}</p>
              ) : null}
            </div>

            {formData.role === 'business' ? (
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                  Company Name
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
                    placeholder="Enter your company name"
                    required
                  />
                </div>
              </div>
            ) : null}

            <button
              onClick={handleNext}
              className="w-full brand-gradient-btn py-3 px-4 rounded-lg font-semibold flex items-center justify-center"
            >
              Next
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
                  placeholder="Create a password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 rounded border-[var(--color-border)] bg-[var(--color-input-bg)] text-[var(--color-primary)] focus:ring-[var(--color-ring)] focus:ring-offset-0 shrink-0"
                aria-label="Agree to Terms and Conditions and Privacy Policy"
              />
              <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
                I agree to the{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium"
                >
                  Terms and Conditions
                </Link>
                {' '}and{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            <div className="flex space-x-4">
                <button
                onClick={handleBack}
                className="w-full bg-[var(--color-surface-muted)] text-[var(--color-text)] border border-[var(--color-border)] py-3 px-4 rounded-lg font-semibold hover:opacity-90 transition-colors flex items-center justify-center"
                >
                Back
                </button>
                <button
                onClick={handleFinalSubmit}
                disabled={loading || !agreedToTerms}
                className="w-full brand-gradient-btn py-3 px-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                {loading ? (
                    <div className="w-5 h-5 border-2 border-[var(--color-text-inverse)] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                    <>
                    Create Account
                    <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                )}
                </button>
            </div>
          </>
        )}
      </div>

      {/* <div className="mt-6">
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
            <div className="flex items-center text-sm text-green-300 overflow-hidden" style={{ height: '20px' }}>
              <span className="font-medium text-[var(--color-text)] mr-2 flex-shrink-0">Free Plan Includes:</span>
              <div className="relative flex-grow h-full">
                {freePlanFeatures.map((feature, index) => (
                  <span
                    key={index}
                    className={`absolute inset-0 whitespace-nowrap transition-all duration-500 ease-in-out ${
                      currentFeatureIndex === index
                        ? 'opacity-100 transform-none'
                        : 'opacity-0 transform translate-y-2'
                    }`}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div> */}

      <div className="mt-6 text-center">
        {/* Only show toggle link in modal mode */}
        {!isPage && (
          <p className="text-[var(--color-text-muted)]">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium"
            >
              Sign In
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

// Main Auth Modal Component
export const AuthModal = ({ isOpen, onClose, defaultMode = 'login' }) => {
  const [mode, setMode] = useState(defaultMode);

  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode, isOpen]);

  if (!isOpen) return null;

  const handleSuccess = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'var(--color-overlay)' }}>
      <div className="bg-[var(--color-surface)] backdrop-blur-lg border border-[var(--color-border)] rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
        <div className="p-6 sm:p-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)] z-10"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {mode === 'login' ? (
            <LoginComponent
              onSwitchToRegister={() => setMode('register')}
              onSuccess={handleSuccess}
              isPage={false}
              onSwitchToForgot={() => setMode('forgot-password')}
            />
          ) : mode === 'register' ? (
            <RegisterComponent
              onSwitchToLogin={() => setMode('login')}
              onSuccess={handleSuccess}
              isPage={false}
            />
          ) : mode === 'forgot-password' ? (
            <ForgotPasswordComponent
              onBack={() => setMode('login')}
              onSuccess={() => setMode('login')}
              isModal={true}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--color-text-muted)]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center max-w-md">
          <Shield className="w-16 h-16 text-[var(--color-primary)] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2">Authentication Required</h2>
          <p className="text-[var(--color-text-muted)] mb-6">Please sign in to access this feature.</p>
          <AuthModal isOpen={true} onClose={() => {}} defaultMode="login" />
        </div>
      </div>
    );
  }

  return children;
};

// User Menu Component
export const UserMenu = ({ setCurrentPage, theme = 'light', currentPage }) => {
  const { user, logout, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const isDark = theme === 'dark';

  // Check if we're on a public page (homepage folder pages)
  const isPublicPage = ['home', 'about', 'pricing'].includes(currentPage);

  const fetchSubscriptionData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const subData = await apiRequest('subscription/current');
      if (subData) {
        setSubscription(subData);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      // Let's not show a toast here to avoid cluttering the UI
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggle = () => {
    if (isPublicPage) {
      // On public pages, directly navigate to profile
      setCurrentPage('profile');
      return;
    }
    
    // On other pages, show/hide dropdown
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen) {
      fetchSubscriptionData();
    }
  };

  if (!user) return null;
  
  const planName = subscription?.subscription_plan || user.subscription_plan;
  const usageCount = subscription?.contracts_this_month ?? user.contracts_this_month;
  const usageLimit = subscription?.monthly_limit ?? user.monthly_limit;
  const usagePercentage = usageLimit > 0 ? Math.min((usageCount / usageLimit) * 100, 100) : 0;

  return (
    <>
      <div className="relative">
        <button
          onClick={handleToggle}
          className={`flex items-center space-x-3 p-2 rounded-lg transition-colors ${
            isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
          }`}
        >
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
            {user.full_name.charAt(0).toUpperCase()}
          </div>
          <div className="text-left hidden sm:block">
            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</div>
            <div className={`text-xs capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{planName} Plan</div>
          </div>
        </button>

        {/* Only show dropdown on non-public pages */}
        {!isPublicPage && isOpen && (
          <div className={`absolute right-0 mt-2 w-64 rounded-lg shadow-lg border py-2 z-50 ${
            isDark
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}>
            <div className={`px-4 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.email}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {loading ? 'Loading usage...' : `${usageCount}/${usageLimit} contracts this month`}
              </p>
              <div className="mt-2">
                <div className={`w-full rounded-full h-1.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div 
                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${usagePercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            {/* Menu Items */}
            <div className="py-1">
              <button
                onClick={() => {
                  setCurrentPage('profile');
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  isDark 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Profile Settings
              </button>
              
              <button
                onClick={() => {
                  setCurrentPage('subscription');
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  isDark 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Subscription
              </button>
              
              {isAdmin && (
                <button
                  onClick={() => {
                    setCurrentPage('admin');
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    isDark 
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Admin Panel
                </button>
              )}
              
              <div className={`border-t my-1 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}></div>
              
              <button
                onClick={() => {
                  logout();
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  isDark 
                    ? 'text-red-400 hover:bg-gray-700' 
                    : 'text-red-600 hover:bg-gray-100'
                }`}
              >
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};